// Records the voice sample (components/Test/VoiceSampleStep.jsx) the same way the video interview
// records its answers, which works on phones and desktops: a copy of the exam's one shared
// microphone (utils/sharedMic.js - opening the microphone a second time mutes the first capture on
// phones) and the browser's MediaRecorder in a format it supports (WebM, or MP4 on iPhone / Safari -
// utils/audioRecordingType.js). The file is uploaded and converted by the backend
// (POST /test/voice-sample), like a video answer. No Web Audio processing in the browser.
import { acquireMicTrack, releaseSharedMicIfUnused } from "./sharedMic";
import { createAudioRecorder, recordedAudioType } from "./audioRecordingType";

export class VoiceSampleError extends Error {
  constructor(kind, detail) {
    super(detail || kind);
    this.kind = kind;
  }
}

// Starts recording. Resolves with { stop(): Promise<Blob>, cancel() } once the recorder runs;
// rejects with VoiceSampleError (denied | unsupported).
export async function startVoiceSampleRecording() {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || !window.MediaRecorder) {
    throw new VoiceSampleError("unsupported");
  }

  let track;
  try {
    track = await acquireMicTrack();
  } catch (error) {
    const denied = error && (error.name === "NotAllowedError" || error.name === "PermissionDeniedError" || error.name === "SecurityError");
    throw new VoiceSampleError(denied ? "denied" : "unsupported", error && (error.name || error.message));
  }

  const release = () => {
    track.stop();
    releaseSharedMicIfUnused();
  };

  let recorder;
  try {
    recorder = createAudioRecorder(new MediaStream([track]));
  } catch (error) {
    release();
    throw new VoiceSampleError("unsupported", error && (error.name || error.message));
  }

  const chunks = [];
  recorder.ondataavailable = (event) => {
    if (event.data && event.data.size > 0) chunks.push(event.data);
  };
  // A timeslice, like the video recorder, so a long reading arrives in pieces.
  recorder.start(1000);

  return {
    stop: () =>
      new Promise((resolve) => {
        recorder.onstop = () => {
          release();
          resolve(new Blob(chunks, { type: recordedAudioType(recorder) }));
        };
        if (recorder.state === "inactive") recorder.onstop();
        else recorder.stop();
      }),
    cancel: () => {
      recorder.onstop = null;
      if (recorder.state !== "inactive") recorder.stop();
      release();
    },
  };
}
